// The 'Look' WebGL pipeline (character look, 2026-10-29): Phaser's single-texture pipeline with a second texture, the
// sprite's mask (<texture key>_mask, its pixels' codes packed six to a texel), and a recolour shader (lookPalette.mjs LOOK_FRAG).
// Sprites in one look share one uniforms object (applyLook memoises it), so they batch until the look or mask changes.
import Phaser from 'phaser';
import { LOOK_FRAG } from './lookPalette.mjs';

const MASK_UNIT = 1;

export class LookPipeline extends Phaser.Renderer.WebGL.Pipelines.SinglePipeline {
  constructor(game) {
    super({ game, fragShader: LOOK_FRAG });
    this._uniforms = null;
    this._mask = null;
  }

  boot() {
    super.boot();
    this.set1i('uMaskSampler', MASK_UNIT);
  }

  onActive() {
    // made current again (or the context rebound): another pipeline may have used texture unit 1 since, so bind the
    // mask and uniforms again on the next sprite (onBind would run for every sprite and break batching)
    this._uniforms = null;
    this._mask = null;
  }

  batchSprite(gameObject, camera, parentTransformMatrix) {
    this.manager.set(this, gameObject);
    const data = gameObject.pipelineData || {};
    // the live texture's mask, so a sprite that swaps its texture never draws with the old one's
    const key = `${gameObject.texture.key}_mask`;
    const textures = gameObject.scene.sys.textures;
    const maskSource = textures.exists(key) ? textures.get(key).source[0] : null;
    const mask = maskSource ? maskSource.glTexture : this.renderer.blankTexture;
    if (data.uniforms !== this._uniforms || mask !== this._mask) {
      this.flush();
      if (data.uniforms !== this._uniforms) this._setUniforms(data.uniforms);
      if (mask !== this._mask) {
        this.bindTexture(mask, MASK_UNIT);
        this.gl.activeTexture(this.gl.TEXTURE0);   // the batch draw binds the sprite's texture on the active unit
        // the shader finds an atlas pixel's texel from the two widths (six pixels per texel, lookPalette.mjs)
        this.set1f('uMaskOn', maskSource ? 1 : 0);
        this.set1f('uAtlasW', gameObject.texture.source[0].width);
        this.set1f('uMaskW', maskSource ? maskSource.width : 1);
      }
      this._uniforms = data.uniforms;
      this._mask = mask;
    }
    super.batchSprite(gameObject, camera, parentTransformMatrix);
  }

  _setUniforms(u) {
    const on = (r) => (u && r.on ? 1 : 0);
    if (!u) {
      for (const name of ['uSkinOn', 'uHairOn', 'uIrisOn', 'uBrowOn']) this.set1f(name, 0);
      return;
    }
    for (const [p, r] of [['uSkin', u.skin], ['uHair', u.hair]]) {
      r.stops.forEach((s, i) => this.set3f(`${p}${i}`, s[0], s[1], s[2]));
      this.set3f(`${p}Ref`, r.ref[0], r.ref[1], r.ref[2]);
      this.set1f(`${p}On`, on(r));
    }
    u.skin.art.forEach((s, i) => this.set3f(`uSkinA${i}`, s[0], s[1], s[2]));   // the art's own ramp (mapSkin's ratio)
    this.set3f('uIrisDark', ...u.iris.dark);
    this.set3f('uIrisLight', ...u.iris.light);
    this.set2f('uIrisRef', u.iris.ref[0], u.iris.ref[1]);
    this.set1f('uIrisOn', on(u.iris));
    this.set3f('uBrow', ...u.brow.colour);
    this.set1f('uBrowRef', u.brow.ref);
    this.set1f('uBrowOn', on(u.brow));
  }
}
